// "Opened by" (#1887): the last 100 requests to this job's data view, from
// the activity log, so the data view's use is on record. Managers only: it
// names other people's requests. Each page, filter change and CSV is one
// request.
import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { CaseDataViewAccess } from '../../utils/caseDataView'

interface CaseDataAccessDialogProps {
  open: boolean
  accesses: CaseDataViewAccess[]
  loading: boolean
  error: string | null
  onClose: () => void
}

const result = (a: CaseDataViewAccess) => {
  if (a.status_code === 200) return a.format === 'csv' ? 'CSV export' : 'Viewed'
  if (a.status_code === 403) return 'Refused'
  return `Failed (${a.status_code})`
}

const when = (ms: number) => new Date(ms).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })

const CaseDataAccessDialog: React.FC<CaseDataAccessDialogProps> = ({ open, accesses, loading, error, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Opened by</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The last 100 requests to this job’s data view, newest first. Each page, filter change and CSV export is one request.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {loading && accesses.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
      )}
      {!loading && !error && accesses.length === 0 && (
        <Typography variant="body2">No one has opened it yet.</Typography>
      )}
      {accesses.length > 0 && (
        <Table size="small" aria-label="Data view requests">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Variables</TableCell>
              <TableCell>Filter</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accesses.map((a, i) => (
              <TableRow key={`${a.at}-${i}`}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(a.at)}</TableCell>
                <TableCell>{a.user_email}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{a.varnames}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{a.filter}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {result(a)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </DialogContent>
    <DialogActions>
      <Button color="info" onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
)

export default CaseDataAccessDialog
